<template>
  <div class="attribute-based-settings">
    <div v-if="!isFromBusinessRules && !isFromConstantApproverFilters">
      <div class="p-4">
        <span class="primary">
          <i class="el-icon-info"></i>
          Please select the profile attribute which needs to be matched with the user filled details
          in the attribute based approval levels (in other words, in which Entity the user filled
          details should be matched?)
        </span>
        <br />
        <span style="font-weight: 700; margin-right: 10px">* Profile Attribute:</span>
        <el-select
          v-model="attributeData.selected_profile_attribute"
          clearable
          size="mini"
          placeholder="Select the profile attribute"
          class="mt-1"
          @change="getProfileAttributeFields"
        >
          <el-option
            v-for="(type, index) in (getAllContactTypes.data || getAllContactTypes).filter(
              (e) => e.entity_type == 'INDIVIDUAL'
            )"
            :key="index + 1"
            :value="type._id"
            :label="type.name"
          >
            {{ type.name }}
          </el-option>
        </el-select>
      </div>
      <div
        class="px-4"
        v-loading="loading"
        element-loading-text="Loading the conditions, please wait..."
        element-loading-spinner="el-icon-loading"
      >
        <div class="custom-header" :style="{ backgroundColor: menuBackground }">Conditions</div>
        <div
          v-for="(condition, index) in attributeData.conditions"
          :key="index"
          class="condition-div"
        >
          <el-tooltip content="Give first criteria to this condition" placement="top">
            <el-checkbox v-model="condition.is_criteria" class="gap-between-fields"></el-checkbox>
          </el-tooltip>
          <el-select
            v-model="condition.type"
            placeholder="Select type"
            size="mini"
            clearable
            filterable
            class="gap-between-fields"
          >
            <el-option
              v-if="attributeData.selected_profile_attribute"
              label="Profile Attribute"
              value="profile_attribute"
            ></el-option>
            <el-option label="Form Field" value="form_field"></el-option>
          </el-select>

          <el-select
            v-if="condition.type === 'profile_attribute'"
            v-model="condition.field"
            placeholder="Select profile attribute field"
            size="mini"
            class="gap-between-fields"
            clearable
            filterable
            @change="(changedValue) => checkCurrentFieldData(changedValue, condition)"
          >
            <el-option
              v-for="field in profileAttributeFields"
              :key="field.key"
              :label="field.label"
              :value="field.key"
            ></el-option>
          </el-select>

          <el-select
            v-if="condition.type === 'form_field'"
            v-model="condition.field"
            placeholder="Select Form Field"
            size="mini"
            class="gap-between-fields"
            clearable
            filterable
            @change="(changedValue) => checkCurrentFieldData(changedValue, condition)"
          >
            <el-option
              v-for="field in allFormbuilderFields"
              :key="field.template_key"
              :label="field.label"
              :value="field.template_key"
            ></el-option>
          </el-select>
          <el-select
            v-model="condition.operator"
            size="mini"
            class="gap-between-fields"
            :disabled="!condition.field"
            placeholder="Select Operator"
            clearable
            filterable
          >
            <el-option
              v-for="(operator, index) of getCurrentFieldOperators(condition)"
              :key="index"
              :label="operator.title"
              :value="operator.value"
            ></el-option>
            <el-option
              v-if="condition.input_type == 'SELECT' && condition.data_type == 'ARRAY'"
              value="=text-includes"
              label="Text Includes"
            ></el-option>
          </el-select>
          <!-- Right hand Side -->
          <el-select
            v-model="condition.value_type"
            placeholder="Select type"
            size="mini"
            class="gap-between-fields"
            clearable
            filterable
          >
            <el-option
              v-if="attributeData.selected_profile_attribute"
              label="Profile Attribute"
              value="profile_attribute"
            ></el-option>
            <el-option label="Form Field" value="form_field"></el-option>
            <el-option label="Custom Value" value="custom_value"></el-option>
          </el-select>
          <el-select
            v-if="condition.value_type === 'profile_attribute'"
            v-model="condition.value_from"
            placeholder="Select profile attribute field"
            size="mini"
            class="gap-between-fields"
            clearable
            filterable
            @change="(changedValue) => checkCurrentFieldData(changedValue, condition)"
          >
            <el-option
              v-for="field in profileAttributeFields"
              :key="field.key"
              :label="field.label"
              :value="field.key"
            ></el-option>
          </el-select>

          <el-select
            v-if="condition.value_type === 'form_field'"
            v-model="condition.value_from"
            placeholder="Select Form Field"
            size="mini"
            class="gap-between-fields"
            clearable
            filterable
            @change="(changedValue) => checkCurrentFieldData(changedValue, condition)"
          >
            <el-option
              v-for="field in allFormbuilderFields"
              :key="field.template_key"
              :label="field.label"
              :value="field.template_key"
            ></el-option>
          </el-select>
          <el-input
            v-if="condition.value_type === 'custom_value' && condition.data_type !== 'OBJECT_ID'"
            v-model="condition.value"
            type="text"
            size="mini"
            placeholder="Enter value"
            :style="{ width: '200px' }"
            class="gap-between-fields"
          ></el-input>
          <el-select
            v-if="condition.value_type === 'custom_value' && condition.data_type == 'OBJECT_ID'"
            v-model="condition.value"
            multiple
            collapse-tags
            size="mini"
            filterable
            class="gap-between-fields"
          >
            <el-option
              v-for="(temp, i) in entilistAsOptions(condition)"
              :key="temp._id + '_' + index + '_' + i"
              :value="temp._id"
              :label="getLabel(temp, i)"
            >
              {{ getLabel(temp, i) }}
            </el-option>
          </el-select>

          <el-select
            v-model="condition.query_type"
            placeholder="Select query type"
            size="mini"
            class="gap-between-fields"
            clearable
            :style="{ width: '80px' }"
          >
            <el-option label="and" value="AND"></el-option>
            <el-option label="or" value="OR"></el-option>
          </el-select>
          <el-link
            :underline="false"
            type="primary"
            size="mini"
            @click="removeCondition(index)"
            class="gap-between-fields"
            icon="el-icon-remove-outline"
          ></el-link>
        </div>

        <el-button
          type="primary"
          size="mini"
          @click="addCondition"
          class="w-full"
          icon="el-icon-circle-plus-outline"
        >
          Add Condition
        </el-button>
        <div class="custom-header mt-1" :style="{ backgroundColor: menuBackground }">
          Conflict Management
        </div>
        <div class="condition-div mb-1">
          <el-radio-group
            v-model="attributeData.conflict_management.assign_to_all_matched_users"
            @change="addApproverForConflictManagement"
          >
            <el-radio label="all_matched_users">Assign to all matched users</el-radio>
            <br />
            <el-radio label="take_approver" class="mt-1">
              Assign to below Approver
              <el-tooltip
                content="If NO approver found with the matched condtions, then the form will not skip to other level. Instead, it will be assigned to a default approver or approver group configured."
                placement="top"
              >
                <i class="el-icon-info"></i>
              </el-tooltip>
            </el-radio>
          </el-radio-group>
          <br />
          <!-- if NO Approver found with the matched condtions, then we will take the below configured approver -->
          <!-- if Approvers evaru find avaledu antey skip avakunda kinda default ga manam select cheskunnavalaki form velatadi -->
          <div
            v-if="attributeData.conflict_management.assign_to_all_matched_users == 'take_approver'"
            class="ml-1"
          >
            <el-row
              v-for="(user, i) in attributeData.conflict_management.assigned_approvers"
              :key="'user_' + i"
              class="mt-1"
            >
              <el-col :span="4" class="ml-1">
                <el-select
                  v-model="user.user_type"
                  size="mini"
                  clearable
                  placeholder="Select user type"
                  @change="updateUserType(i)"
                >
                  <el-option
                    v-for="userType in userTypes"
                    :key="userType.value"
                    :label="userType.label"
                    :value="userType.value"
                  ></el-option>
                </el-select>
              </el-col>
              <template v-if="user.user_type == 'COMPANY'">
                <el-col :span="4">
                  <el-select
                    v-model="user.company_user_type"
                    clearable
                    size="mini"
                    @change="(value) => updateCompanyUsers(value, user)"
                    placeholder="Select company user type"
                    class="ml-1"
                  >
                    <el-option
                      v-for="item in getUserTypeList"
                      :key="item._id"
                      :label="getLabel(item)"
                      :value="item._id"
                    ></el-option>
                  </el-select>
                </el-col>
                <el-col :span="4">
                  <el-select
                    v-if="user.company_user_type"
                    v-model="user.preferred_company_users"
                    collapse-tags
                    clearable
                    size="mini"
                    multiple
                    placeholder="Select preferred user"
                    class="ml-1"
                    default-first-option
                  >
                    <el-option
                      v-for="item in getCompanyUsersByType(user.company_user_type)"
                      :key="item._id + '_company_user'"
                      :label="item.email"
                      :value="item._id"
                    >
                      {{ item.email }}
                    </el-option>
                  </el-select>
                </el-col>
                <el-col :span="4" class="mr-1">
                  <el-checkbox
                    class="ml-1"
                    v-if="getCompanyUsersByType(user.company_user_type).length > 1"
                    v-model="user.any_user_can_approve"
                  >
                    Any user can approve
                  </el-checkbox>
                </el-col>
              </template>
              <template v-else-if="user.user_type == 'APPLICATION'">
                <el-col :span="4">
                  <el-select
                    v-model="user.application_user_type"
                    clearable
                    size="mini"
                    @change="updateApplicationUserType(user)"
                    placeholder="Select application user type"
                    class="ml-1"
                  >
                    <el-option
                      v-for="(type, index) in getAllContactTypes.data || getAllContactTypes"
                      :key="index + 1"
                      :value="type._id"
                      :label="type.name"
                    >
                      {{ type.name }}
                    </el-option>
                  </el-select>
                </el-col>
                <el-col :span="4">
                  <div v-if="checkIsBusinessUserType(user.application_user_type)">
                    <el-select
                      v-model="user.business_account_type"
                      clearable
                      size="mini"
                      placeholder="Select business application user type"
                      class="ml-1"
                      @change="getBusinessAccountNames(user.application_user_type)"
                    >
                      <el-option
                        v-for="(type, index) in accountTypes"
                        :key="index + 1"
                        :value="type.value"
                        :label="type.label"
                      >
                        {{ type.label }}
                      </el-option>
                    </el-select>
                  </div>
                </el-col>
                <el-col :span="4">
                  <div
                    class="ml-1"
                    v-if="user.application_user_type && user.business_account_type == 'GROUP'"
                  >
                    <el-select
                      v-model="user.application_user_business_account_id"
                      clearable
                      size="mini"
                      placeholder="Select business account"
                      @change="updatePreferredApplicationUsers(user)"
                    >
                      <el-option
                        v-for="(account, i) in accountNamesObj[user.application_user_type]"
                        :key="i"
                        :value="account._id"
                        :label="getLabelForBusinessAccount(account)"
                      >
                        {{ getLabelForBusinessAccount(account) }}
                      </el-option>
                    </el-select>
                  </div>
                </el-col>
                <el-col :span="4">
                  <div
                    class="ml-1"
                    v-if="user.application_user_type && user.business_account_type !== 'GROUP'"
                  >
                    <el-select
                      v-model="user.preferred_application_users"
                      collapse-tags
                      clearable
                      multiple
                      size="mini"
                      placeholder="Select preferred user"
                      :disabled="user.relational_user"
                    >
                      <el-option
                        v-for="(contact, index) in getUserTypeContacts(user.application_user_type)"
                        :key="index + '_application_user_mail'"
                        :value="contact.email"
                        :label="contact.email"
                      >
                        {{ contact.email }}
                      </el-option>
                    </el-select>
                    <!-- <el-checkbox
                      v-if="checkIsRelational(user.application_user_type)"
                      :disabled="
                        user.preferred_application_users &&
                        user.preferred_application_users.length
                          ? true
                          : false
                      "
                      v-model="user.relational_user"
                      >User relational data</el-checkbox
                    > -->
                  </div>
                </el-col>
                <el-col :span="4" class="mr-1">
                  <el-checkbox
                    class="ml-1"
                    v-if="
                      getUserTypeContacts(user.application_user_type).length > 1 &&
                      !user.relational_user
                    "
                    v-model="user.any_user_can_approve"
                  >
                    Any user can approve
                  </el-checkbox>
                </el-col>
              </template>
            </el-row>
          </div>
        </div>
      </div>
    </div>
    <div
      class="business-rules"
      v-else-if="isFromBusinessRules"
      v-loading="loading"
      element-loading-text="Loading, please wait..."
      element-loading-spinner="el-icon-loading"
    >
      <div v-for="(rule, index) in attributeData.business_rules" :key="index" class="rule-card">
        <!-- Rule Header with Number -->
        <div class="rule-header">
          <div class="rule-number-label">Rule - {{ index + 1 }}</div>
          <el-link
            :underline="false"
            type="text"
            icon="el-icon-remove-outline"
            @click="removeRule(index)"
          ></el-link>
        </div>

        <!-- Rule Content -->
        <div class="rule-content">
          <div class="rule-part">
            <span class="label">When this condition matches:</span>
            <el-select
              v-model="rule.condition_type"
              clearable
              filterable
              placeholder="Select condition type"
              size="mini"
              class="select-field"
            >
              <el-option
                v-for="filter in conditionTypes"
                :key="filter.value"
                :label="filter.label"
                :value="filter.value"
              />
            </el-select>
            <el-tooltip
              v-if="rule.condition_type == 'CONFIGURE_CONDITION'"
              content="Add Conditions"
              placement="top"
            >
              <el-button
                type="text"
                icon="el-icon-circle-plus-outline"
                size="medium"
                @click="addConditionToBusinessRule(rule, index)"
              ></el-button>
            </el-tooltip>
            <el-select
              v-else
              v-model="rule.condition"
              clearable
              filterable
              placeholder="Select condition"
              size="mini"
              class="select-field"
            >
              <el-option
                v-for="filter in formbuilderAllFilters"
                :key="filter._id"
                :label="filter.filter_name"
                :value="filter._id"
              />
            </el-select>

            <!-- Popover with icon as trigger -->
            <el-popover
              placement="top-start"
              title="Conditions:"
              trigger="hover"
              v-if="rule.condition && rule.condition_type == 'SAVED_CONDITION'"
            >
              <!-- This slot is the trigger (the icon) -->
              <i
                slot="reference"
                class="el-icon-view"
                style="cursor: pointer; margin-left: 4px"
              ></i>

              <!-- Popover content -->
              <FormbuilderFilters
                :currentFormbuilder="formBuilder"
                :formbuilderFiltersData="getFormbuilderFiltersData(rule.condition)"
                :allFormbuilderFields="getFormbuilderAllMappedFields"
                :fromBusinessRules="true"
              />
            </el-popover>
          </div>
          <div class="rule-part">
            <span class="label">then execute the action:</span>
            <el-select
              v-model="rule.action"
              placeholder="Choose action"
              size="mini"
              class="select-field"
              @change="getNextLevelsData"
              width="120"
            >
              <el-option label="Complete workflow" value="COMPLETE" />
              <el-option label="Terminate workflow" value="TERMINATE" />
              <el-option label="Skip to" value="SKIP" />
            </el-select>
          </div>

          <!-- Skip Target (if applicable) -->
          <div v-if="rule.action === 'SKIP'">
            <el-select
              v-model="rule.skip_to"
              placeholder="Select the level"
              size="mini"
              class="select-field"
            >
              <el-option
                v-for="(level, index) in getNextLevelsData"
                :key="index"
                :label="level.title"
                :value="'levelIndex - ' + level.level_index"
              />
            </el-select>
          </div>
        </div>

        <!-- Note inside card -->
        <p class="rule-card-note">
          Note: Other rulesets will not be applicable when this ruleset matching
        </p>
      </div>

      <!-- Add Rule Button -->
      <el-button plain size="mini" @click="addRule" icon="el-icon-circle-plus-outline">
        Add Rule
      </el-button>
    </div>
    <div v-else-if="isFromConstantApproverFilters">
      <EntityFilter
        :currentEntity="currentEntity"
        :entityFiltersData="attributeData.constant_approvers[constantApproverIndex].filters_data"
        :entityUserFiltersData="
          attributeData.constant_approvers[constantApproverIndex].filters_data
        "
        :selectedEntityFields="selectedEntityFields"
        :fromConstApproverFilters="true"
      ></EntityFilter>
    </div>
    <dialog-component
      :title="'Add Conditions'"
      :visible="openFormbuilderFiltersDialog"
      @before-close="closeFormbuilderFiltersModel"
      :isShowFooter="true"
      :containerWidth="'65%'"
      :containerHeight="'70%'"
    >
      <div
        class="p-4"
        v-if="attributeData?.business_rules?.[currentBusinessRuleIndex]?.conditions_data"
      >
        <FormbuilderFilters
          :currentFormbuilder="formBuilder"
          :formbuilderFiltersData="
            attributeData.business_rules[currentBusinessRuleIndex].conditions_data
          "
          :allFormbuilderFields="getFormbuilderAllMappedFields"
          :fromBusinessConditions="true"
        />
      </div>
    </dialog-component>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { fetchEntityById } from '../../repo/entityRepo';
  import { postAPICall } from '../../helpers/httpHelper';
  import ApprovalsHelper from '@/mixins/ApprovalsHelper';
  import { getAllFieldsArrayFromEntity } from '../../utils/formatter';
  import { fetchAllFormbuilderFiltersById } from '@/repo/filtersRepo';

  export default {
    name: 'AttributeBasedSettings',
    props: {
      getAllContactTypes: Array,
      attributeData: Object,
      allFormbuilderFields: Array,
      allEntities: Array,
      isFromBusinessRules: Boolean,
      currentLevelIndex: Number,
      company_users: Array,
      allContacts: Array,
      formBuilder: Object,
      constantApproverIndex: Number,
      isFromConstantApproverFilters: Boolean,
      selectedEntityFields: Array,
      currentEntity: Object,
    },
    data() {
      return {
        currentBusinessRuleIndex: -1,
        openFormbuilderFiltersDialog: false,
        conditionTypes: [
          { value: 'SAVED_CONDITION', label: 'Saved Conditions' },
          { value: 'CONFIGURE_CONDITION', label: 'Configure Conditions' },
        ],
        entityFiltersData: {
          query_type: 'AND',
          filters: [],
          filter_name: '',
          entity_id: '',
          isSaveFilter: false,
          columns_settings: [],
          quick_filters: [],
          displayType: '',
          data_limit: 10,
          email_template_id: '',
          email_view_fields: [],
          sortBy: '',
          sortOrder: '',
        },
        entityUserFiltersData: {
          parentFilterId: '',
          query_type: 'AND',
          filters: [],
          filter_name: '',
          isSaveFilter: false,
          data_limit: 10,
          columns_settings: [],
        },
        contentFields: [
          'HEADING',
          'PARAGRAPH',
          'HTML_CONTENT',
          'SINGLE_LINE_CONTENT',
          'VIDEO',
          'ACTION_BUTTON',
          'HORIZONTAL_LINE',
          'DIVISION',
        ],
        formbuilderAllFilters: [],
        loading: false,
        profileAttributeFields: [],
        getEntityRecordsForTable: null,
        entityDataInfo: [],
        accountNamesObj: {},
        mappedEntityPrimaryFields: {},
        operatorsMap: {
          NUMBER: [
            {
              title: 'Equals (=)',
              value: '=',
            },
            {
              title: 'Greater Than (>)',
              value: '>',
            },
            {
              title: 'Greater Than or Equal(>=)',
              value: '>=',
            },
            {
              title: 'Less Than (<)',
              value: '<',
            },
            {
              title: 'Less Than or Equal(<=)',
              value: '<=',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Range',
              value: 'between',
            },
          ],
          PHONE: [
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
          ],
          CONCATENATE: [
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
          ],
          STRING: [
            {
              title: 'Equals (Case-sensitive)',
              value: '=case-sensitive',
            },
            {
              title: 'Equals (Case-insensitive)',
              value: '=case-insensitive',
            },
            {
              title: 'Text Includes',
              value: '=text-includes',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Starts With',
              value: '=starts-with',
            },
            {
              title: ' Not Starts With',
              value: '=not-starts-with',
            },
            {
              title: 'Ends With',
              value: '=ends-with',
            },
            {
              title: 'Not Ends With',
              value: '!ends-with',
            },
          ],
          PAYMENT: [
            {
              title: 'In',
              value: 'in',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
            {
              title: 'Equals (Case-sensitive)',
              value: '=case-sensitive',
            },
            {
              title: ' Not Equals (Case-sensitive)',
              value: '!=case-sensitive',
            },
            {
              title: 'Equals (Case-insensitive)',
              value: '=case-insensitive',
            },
            {
              title: ' Not Equals (Case-insensitive)',
              value: '!=case-insensitive',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
          ],
          OBJECT_ID: [
            {
              title: 'In',
              value: 'in',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
          ],
          DATE: [
            {
              title: 'Real time picker',
              value: 'real_time',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
            {
              title: 'Match with weekdays',
              value: 'weekdays',
            },
            {
              title: 'Equals (=)',
              value: '=',
            },
            {
              title: 'Greater Than (>)',
              value: '>',
            },
            {
              title: 'Greater Than or Equal(>=)',
              value: '>=',
            },
            {
              title: 'Less Than (<)',
              value: '<',
            },
            {
              title: 'Less Than or Equal(<=)',
              value: '<=',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Between',
              value: 'between',
            },
            {
              title: 'NotBetween',
              value: 'notbetween',
            },
            {
              title: 'Before',
              value: 'before',
            },
            {
              title: 'After',
              value: 'after',
            },
          ],
          TIME: [
            {
              title: 'Equals (=)',
              value: '=',
            },
            {
              title: 'Greater Than (>)',
              value: '>',
            },
            {
              title: 'Greater Than or Equal(>=)',
              value: '>=',
            },
            {
              title: 'Less Than (<)',
              value: '<',
            },
            {
              title: 'Less Than or Equal(<=)',
              value: '<=',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Between',
              value: 'between',
            },
            {
              title: 'NotBetween',
              value: 'notbetween',
            },
          ],
          RELATION: [
            {
              title: 'In',
              value: 'in',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Filter',
              value: 'filter',
            },
          ],
          FILE: [
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
          ],
          ARRAY: [
            {
              title: 'In',
              value: 'in',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
          ],
          DATE_TIME: [
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Date Includes',
              value: 'dateinclude',
            },
            {
              title: 'Date Not Includes',
              value: 'notdateinclude',
            },
          ],
          WEEKDAYS: [
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'In',
              value: 'in',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
          ],
          TIME_RANGE: [
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Time includes',
              value: 'timeinclude',
            },
          ],
          YES_OR_NO: [
            {
              title: 'Equals',
              value: '=case-sensitive',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
          ],
          CURRENCY: [
            {
              title: 'Equals (=)',
              value: '=',
            },
            {
              title: 'Greater Than (>)',
              value: '>',
            },
            {
              title: 'Greater Than or Equal(>=)',
              value: '>=',
            },
            {
              title: 'Less Than (<)',
              value: '<',
            },
            {
              title: 'Less Than or Equal(<=)',
              value: '<=',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Range',
              value: 'between',
            },
          ],
          BOOLEAN: [
            {
              title: 'In',
              value: 'in',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
          ],
          DATE_RANGE: [
            {
              title: 'Exist',
              value: 'exists',
            },
            {
              title: 'Not Exist',
              value: 'notexists',
            },
            {
              title: 'Date includes',
              value: 'dateinclude',
            },
          ],
        },
        operatorValueTypeMap: [
          {
            value_type: 'VALUE',
            operators: [
              '=',
              '<=',
              '<',
              '>',
              '>=',
              '=case-insensitive',
              '=case-sensitive',
              '=text-includes',
              '!=',
              'dateinclude',
              'timeinclude',
              'in',
              'nin',
              '=starts-with',
              '=not-starts-with',
              '=ends-with',
              '!ends-with',
              'after',
              'before',
            ],
            data_types: [
              'NUMBER',
              'STRING',
              'DATE',
              'TIME',
              'DATE_TIME',
              'TIME_RANGE',
              'YES_OR_NO',
              'CURRENCY',
              'LIST',
              'WEEKDAYS',
              'BOOLEAN',
              'created_at',
              'updated_at',
              'created_by',
              'updated_by',
            ],
          },
          {
            value_type: 'GLOBAL_VARIABLE',
            operators: [
              '=',
              '<=',
              '>=',
              '<',
              '>',
              '=case-insensitive',
              '=text-includes',
              '=case-sensitive',
              '!=',
            ],
            data_types: ['NUMBER', 'STRING'],
          },
          {
            value_type: 'NEW_LIST',
            operators: ['in', 'nin'],
            data_types: ['NUMBER'],
          },

          {
            value_type: 'ENTITY_DATA_LIST',
            operators: ['in', 'nin'],
            data_types: ['OBJECT_ID', 'RELATION'],
          },
          {
            value_type: 'REAL_TIME_PICKER',
            operators: ['dateinclude'],
            data_types: ['DATE_TIME'],
          },
          {
            value_type: 'CURRENT_DATE',
            operators: ['=', '<=', '<', '>', '>='],
            data_types: ['DATE_TIME', 'DATE'],
          },
        ],
        menuBackground: '#1B487E',
        userTypes: [
          { value: 'APPLICATION', label: 'Application Users' },
          { value: 'COMPANY', label: 'Company Users' },
        ],
        accountTypes: [
          { value: 'GROUP', label: 'Group' },
          { value: 'SPECIFIC_USER', label: 'Specific User' },
        ],
      };
    },
    mixins: [ApprovalsHelper],
    components: {
      EntityFilter: () => import('../../components/entity/entityFilters.vue'),
      FormbuilderFilters: () => import('./formbuilderFilters.vue'),
    },
    computed: {
      ...mapGetters('company', ['getCompanyDetails']),
      getFormbuilderAllMappedFields() {
        return this.allFormbuilderFields.map((f) => ({
          ...f,
          key: f.template_key,
        }));
      },
      getNextLevelsData() {
        console.log('Current level index:', this.currentLevelIndex);

        let allLevels = this.formBuilder.advanced_approval_users || [];

        allLevels = allLevels.map((level, index) => {
          const role = (level?.role || '').replace(/_/g, ' ').toUpperCase();
          const approverType = (level?.approver_type || '').replace(/_/g, ' ').toUpperCase();

          return {
            ...level,
            title: `Level${index + 1} - ${role} - ${approverType}`,
            level_index: index,
          };
        });

        const nextLevelsData = allLevels.slice(this.currentLevelIndex + 1);
        return nextLevelsData;
      },
    },
    async mounted() {
      this.loading = true;
      this.menuBackground = this.getCompanyDetails.menubackground;
      if (this.attributeData?.selected_profile_attribute && !this.isFromBusinessRules) {
        await this.getProfileAttributeFields();
      }
      if (this.attributeData?.conditions?.length) {
        this.attributeData.conditions.forEach(async (condition) => {
          if (condition.value_type === 'custom_value' && condition.data_type == 'OBJECT_ID') {
            await this.checkCurrentFieldData(condition.field, condition);
          }
        });
      }
      this.formbuilderAllFilters = await fetchAllFormbuilderFiltersById({
        formbuilderId: this.$route.params.form_builder_id,
      });
      if (this.isFromBusinessRules && this.attributeData?.business_rules?.length == 0) {
        this.addRule();
      }
      this.loading = false;
    },
    methods: {
      addConditionToBusinessRule(rule, index) {
        this.currentBusinessRuleIndex = index;
        this.openFormbuilderFiltersDialog = true;
      },
      closeFormbuilderFiltersModel() {
        this.openFormbuilderFiltersDialog = false;
      },
      addCondition() {
        this.attributeData.conditions.push({
          type: '',
          field: '',
          operator: '',
          value_type: '',
          value_from: '',
          value: null,
          query_type: 'AND',
          is_criteria: false,
        });
      },
      removeCondition(index) {
        this.attributeData.conditions.splice(index, 1);
      },
      // addConditionToRuleset(rulesetIndex) {
      //   let newCondition = {
      //     type: "",
      //     field: "",
      //     operator: "",
      //     value_type: "",
      //     value_from: "",
      //     value: null,
      //     query_type: "AND",
      //   };

      //   this.attributeData.business_rules[rulesetIndex].conditions.push(
      //     newCondition
      //   );
      // },
      // removeConditionFromRuleset(rulesetIndex, conditionIndex) {
      //   this.attributeData.business_rules[rulesetIndex].conditions.splice(
      //     conditionIndex,
      //     1
      //   );
      // },

      getFormbuilderFiltersData(filterId) {
        return this.formbuilderAllFilters.find((f) => f._id == filterId);
      },
      addRule() {
        let filter = {
          type: '',
          field: '',
          operator: '',
          value_type: '',
          value_from: '',
          value: null,
          query_type: 'AND',
        };

        this.attributeData.business_rules.push({
          condition_type: 'SAVED_CONDITION',
          condition: '',
          action: '',
          skip_to: '',
          conditions_data: {
            filters: [filter],
            filter_name: '',
            formbuilder_id: this.formBuilder._id,
            query_type: 'AND',
            isSaveFilter: false,
          },
        });
      },
      removeRule(index) {
        this.attributeData.business_rules.splice(index, 1);
      },
      addApproverForConflictManagement() {
        if (
          this.attributeData.conflict_management.assign_to_all_matched_users == 'take_approver' &&
          !this.attributeData?.conflict_management?.assigned_approvers?.length
        ) {
          this.attributeData.conflict_management.assigned_approvers?.push({});
        }
      },
      async getProfileAttributeFields() {
        this.loading = true;
        let fields = [];
        let entity = await fetchEntityById(this.attributeData.selected_profile_attribute);
        if (entity) {
          entity.templates.forEach((tem) => {
            fields = [...fields, ...this.getFieldsFromTemplate(tem.templateInfo, entity)];
          });
        }
        this.profileAttributeFields = fields;
        this.loading = false;
      },
      getFieldsFromTemplate(data, entity) {
        let fields = [];
        let tempData = JSON.parse(JSON.stringify(data));
        let contentFields = [
          'HEADING',
          'PARAGRAPH',
          'HTML_CONTENT',
          'SINGLE_LINE_CONTENT',
          'VIDEO',
          'ACTION_BUTTON',
          'HORIZONTAL_LINE',
          'DIVISION',
        ];
        tempData.sections.forEach((e) => {
          fields = [
            ...fields,
            ...e.fields.filter((e) => contentFields.indexOf(e.inputType) === -1),
          ];
        });
        fields = fields.map((e) => {
          e.template_id = data._id;
          e.template_name = data.name;
          if (!e.template_key) {
            e.template_key = e.template_id + '#' + e.key;
          }
          if (!e.mention_key) {
            e.mention_key = e.template_id + '#' + e.key;
          }
          if (e.key && !e.key.includes('#')) {
            e.key = e.template_id + '#' + e.key;
          }
          e.label = entity.name + ' - ' + e.label;
          return e;
        });
        return fields;
      },
      async checkCurrentFieldData(fieldKey, filter) {
        if (fieldKey == 'status') {
          filter.data_type = 'STATUS';
          filter.data_source = 'VALUE';
          return;
        }
        if (fieldKey == 'isCreatedFromFormbuilder') {
          filter.data_type = 'isCreatedFromFormbuilder';
          filter.data_source = 'VALUE';
          return;
        }
        if (fieldKey == 'created_at') {
          filter.data_type = 'DATE';
          filter.input_type = 'created_at';

          return;
        }
        if (fieldKey == 'updated_at') {
          filter.data_type = 'DATE';
          filter.input_type = 'updated_at';

          return;
        }
        if (fieldKey == 'created_by') {
          filter.data_type = 'created_by';
          filter.input_type = 'created_by';

          return;
        }
        if (fieldKey == 'updated_by') {
          filter.data_type = 'updated_by';
          filter.input_type = 'updated_by';

          return;
        }
        let field = (
          filter.type == 'profile_attribute' || filter.value_type == 'profile_attribute'
            ? this.profileAttributeFields
            : this.allFormbuilderFields
        ).find((field) => field.key == fieldKey || field.template_key == fieldKey);
        if (field.inputType == 'DATE_TIME' || field.inputType == 'DATE') {
          filter.is_date_time = true;
        } else {
          filter.is_date_time = false;
        }
        if (field?.data_table_key) {
          filter.data_table_key = field.data_table_key;
        }
        if (field.input_type == 'WEEKDAYS') {
          filter.allow_multiple = field.allow_multiple;
        } else if (field.input_type == 'FORMULA') {
          filter.data_type = field.result_type;
          filter.is_formula = true;
        } else if (field.input_type == 'ENTITY_VARIABLE' && field.inputType == 'FORMULA') {
          filter.data_type = this.getTypeFromInputType(field.result_type);
          field.data_type = this.getTypeFromInputType(field.result_type);
        } else {
          filter.data_type = this.getTypeFromInputType(field.inputType);
          field.data_type = this.getTypeFromInputType(field.inputType);
        }
        if (field.input_type == 'RELATION' || field.data_type == 'OBJECT_ID') {
          filter.relation_entity_id = field.entity_id;
        }
        this.$set(filter, 'data_source', '');
        // console.log(
        //   "Fieldddddddddddd",
        //   filter.field,
        //   field.input_type,
        //   filter.data_type,
        //   filter
        // );
        if (field.inputType == 'SELECT' || field.inputType == 'MULTI_SELECT') {
          this.$set(filter, 'value', []);
        }
        // ENtity type select fields are becoming emtpty if value have array of entity data ids.
        // else {
        //   this.$set(filter, "value", "");
        // }
        filter.input_type = field.input_type;
        await this.getEntilistAsOptions(filter);
      },
      async getEntilistAsOptions(filter) {
        let fieldKey = filter.field;
        let field = (
          filter.type == 'profile_attribute' || filter.value_type == 'profile_attribute'
            ? this.profileAttributeFields
            : this.allFormbuilderFields
        ).find((field) => field.key == fieldKey || field.template_key == fieldKey);
        if (field.inputType == 'ENTITY' || field.input_type == 'RELATION') {
          await this.fetchEntityPrimaryFieldsData(field.entity_id);
        }
      },
      async fetchEntityPrimaryFieldsData(entity_id) {
        let currentEntity = await fetchEntityById(entity_id);
        let params = {
          entity_id: entity_id,
          template_fields_data: getAllFieldsArrayFromEntity(currentEntity),
          format_data: true,
          limit: 100,
          filters: [],
        };
        const v2Response = await postAPICall('POST', '/entities-data/entity/data/v2', params);
        this.getEntityRecordsForTable = v2Response?.data;
        if (this.getEntityRecordsForTable && this.getEntityRecordsForTable.data) {
          this.entityDataInfo.push({
            entity: currentEntity,
            data: this.getEntityRecordsForTable.data,
            entity_id: entity_id,
          });
        }
      },
      getCurrentFieldOperators(filter) {
        if (
          filter.field == 'status' ||
          filter.data_type == 'STATUS' ||
          filter.field == 'isCreatedFromFormbuilder'
        ) {
          return [
            {
              title: 'In',
              value: 'in',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
          ];
        }
        if (filter.data_type == 'created_at' || filter.data_type == 'updated_at') {
          return [
            {
              title: 'Greater Than (>)',
              value: '>',
            },
            {
              title: 'Less Than (<)',
              value: '<',
            },
            {
              title: 'Last Month',
              value: 'lastmonth',
            },
            {
              title: 'This Month',
              value: 'thismonth',
            },
          ];
        }
        if (filter.data_type == 'created_by' || filter.data_type == 'updated_by') {
          return [
            {
              title: 'In',
              value: 'in',
            },
            {
              title: 'Not In',
              value: 'nin',
            },
          ];
        }
        if (filter.field) {
          if (filter.data_type) {
            return this.operatorsMap[filter.data_type];
          } else {
            let field = (
              filter.type == 'profile_attribute' || filter.value_type == 'profile_attribute'
                ? this.profileAttributeFields
                : this.allFormbuilderFields
            ).find((field) => field.key == filter.field || field.template_key == filter.field);
            if (field && field.data_type && field.result_type && field.result_type.length) {
              return this.operatorsMap[field.result_type];
            } else if (field && field.data_type) {
              return this.operatorsMap[field.data_type];
            } else {
              return [];
            }
          }
        } else {
          return [];
        }
      },
      getTypeFromInputType(inputType) {
        const obj = {
          SINGLE_LINE_TEXT: 'STRING',
          MULTI_LINE_TEXT: 'STRING',
          RANDOM_TEXT: 'STRING',
          DATE: 'DATE',
          DATE_TIME: 'DATE',
          TIME: 'TIME',
          NUMBER: 'NUMBER',
          FILE: 'FILE',
          SELECT: 'ARRAY',
          YES_OR_NO: 'YES_OR_NO',
          MULTI_SELECT: 'ARRAY',
          CHECKBOX_GROUP: 'ARRAY',
          RADIO_BUTTON_GROUP: 'ARRAY',
          CHECKBOX: 'BOOLEAN',
          RADIO: 'STRING',
          HEADING: 'STRING',
          SIGNATURE: 'FILE',
          GLOBAL_VARIABLE: 'OBJECT_ID',
          TEMPLATE_FIELD: 'OBJECT_ID',
          ENTITY: 'OBJECT_ID',
          IMAGE: 'STRING',
          PASSWORD: 'STRING',
          MASKED: 'STRING',
          EMAIL: 'EMAIL',
          PHONE: 'PHONE',
          FAX: 'NUMBER',
          ZIP_CODE: 'ZIP_CODE',
          CONCATENATE: 'CONCATENATE',
          CURRENCY: 'NUMBER',
          PHONE_COUNTRY_CODE: 'PHONE',
          QUESTION: 'STRING',
          DATE_TIME_RANGE: 'DATE_TIME',
          DATE_RANGE: 'DATE_RANGE',
          WEEKDAYS: 'WEEKDAYS',
          TIME_RANGE: 'TIME_RANGE',
          RELATION: 'RELATION',
          LIST: 'ARRAY',
          Hyper_link: 'STRING',
          STAR_RATING: 'ARRAY',
          PAY_BUTTON: 'PAYMENT',
          AGGREGATE_FUNCTION: 'NUMBER',
          AUTO_INCREMENT_NUMBER: 'STRING',
        };
        return obj[inputType];
      },
      entilistAsOptions(filter) {
        let field = (
          filter.type === 'profile_attribute' || filter.value_type === 'profile_attribute'
            ? this.profileAttributeFields
            : this.allFormbuilderFields
        ).find((e) => e.key == filter.field || e.template_key == filter.field);
        if (field && field.entity_id && this.entityDataInfo && this.entityDataInfo.length) {
          let selectedEntity = this.entityDataInfo.find((e) => e.entity_id === field.entity_id);
          if (selectedEntity && selectedEntity.data) {
            return selectedEntity.data;
          }
        }

        return [];
      },
      getLabel(item, i) {
        let selectedEntity = (this.entityDataInfo || []).find((e) => e.entity_id == item.entity_id);
        if (selectedEntity && selectedEntity.entity) {
          selectedEntity = selectedEntity.entity;
        }
        let name =
          item &&
          item.entity_prime_data &&
          Object.keys(item.entity_prime_data) &&
          item.entity_prime_data[Object.keys(item.entity_prime_data)[0]]
            ? item.entity_prime_data[Object.keys(item.entity_prime_data)[0]]
            : 'data ' + i;
        if (selectedEntity && selectedEntity.primaryFields) {
          let primaryField = selectedEntity.primaryFields[0];
          let key = primaryField && primaryField['key'] ? primaryField['key'] : null;
          if (key) {
            name =
              item.entityData &&
              primaryField.template_id &&
              item.entityData[primaryField.template_id]
                ? item.entityData[primaryField.template_id][key + '/name']
                  ? item.entityData[primaryField.template_id][key + '/name']
                  : item.entityData[primaryField.template_id][key]
                    ? item.entityData[primaryField.template_id][key]
                    : ''
                : '';
            if (!name && item.entity_prime_data) {
              name = item.entity_prime_data[key + '/name']
                ? item.entity_prime_data[key + '/name']
                : item.entity_prime_data[key];
            }
          }
        }
        return name;
      },
      getLabelForBusinessAccount(data) {
        if (
          data.entity_id &&
          data.entityData &&
          this.mappedEntityPrimaryFields[data.entity_id] &&
          this.mappedEntityPrimaryFields[data.entity_id].includes('#')
        ) {
          let [tempId, key] = this.mappedEntityPrimaryFields[data.entity_id].split('#');
          if (data.entityData[tempId] && data.entityData[tempId][key]) {
            return data.entityData[tempId][key + '/name']
              ? data.entityData[tempId][key + '/name']
              : data.entityData[tempId][key];
          } else if (data.entity_prime_data[key]) {
            return data.entity_prime_data[key];
          } else {
            return Object.keys(data.entity_prime_data) &&
              Object.keys(data.entity_prime_data)[0] &&
              data.entity_prime_data[Object.keys(data.entity_prime_data)[0]]
              ? data.entity_prime_data[Object.keys(data.entity_prime_data)[0]]
              : '';
          }
        }
      },
      async updatePreferredApplicationUsers(user) {
        let params = {
          contactType: user.application_user_type,
          accountName: user.application_user_business_account_id,
          limit: 10,
          page: 1,
        };
        const response = await postAPICall(
          'GET',
          `/contacts/${params.contactType}/${params.accountName}/list`,
          params
        );
        let contacts = response?.data || [];
        user.preferred_application_users = contacts?.map((c) => c.email);
      },
      async getBusinessAccountNames(contactType) {
        let params = {
          contact_types: typeof contactType == 'string' ? [contactType] : contactType,
        };
        const data = await postAPICall('POST', '/entity/account-names', params);
        this.accountNamesObj = data.data;
        this.getAllContactTypes?.forEach((e) => {
          this.mappedEntityPrimaryFields[e._id] =
            e.primaryFields &&
            e.primaryFields[0] &&
            e.primaryFields[0].key &&
            e.primaryFields[0].template_id
              ? e.primaryFields[0].template_id + '#' + e.primaryFields[0].key
              : '';
        });
      },
    },
  };
</script>

<style scoped>
  .custom-header {
    color: #ffffff;
    text-align: center;
    font-weight: 600;
    font-size: 1.125rem;
    line-height: 1.75rem;
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
  }

  .condition-div {
    border: 0.25px solid #ccc;
    border-radius: 4px;
    padding: 12px;
  }

  .gap-between-fields {
    margin-left: 7px;
  }

  .business-rules {
    font-family: 'Segoe UI', Arial, sans-serif;
    font-size: 13px;
    color: #333;
    margin: 0 auto;
  }

  .rule-card {
    background: #ffffff;
    border: 1px solid #ddd;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 12px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
    padding: 0;
  }

  .rule-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: #e3f2fd;
    padding: 12px 16px;
    border-bottom: 1px solid #ddd;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    margin: 0;
  }

  .rule-number-label {
    color: #1976d2;
    font-weight: 600;
    font-size: 13px;
    text-align: center;
  }

  .rule-content {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    padding: 12px 16px;
    margin-left: 20px;
  }

  .rule-part {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .label {
    font-weight: 500;
    color: #444;
    white-space: nowrap;
    font-size: 13px;
  }

  .select-field {
    min-width: 150px;
  }

  .input-field {
    min-width: 120px;
  }

  .rule-card-note {
    font-size: 11px;
    color: #777;
    margin: 0;
    padding: 8px 16px;
    border-top: 1px dashed #eee;
    background: #fafafa;
  }

  .add-rule-btn {
    width: 100%;
    font-size: 13px;
    margin-top: 12px;
  }
</style>
